import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { ProjectOverviewEntry } from '@/types/dashboard';

const STATUS_COLORS: Record<string, 'primary' | 'warning' | 'success' | 'neutral'> = {
  in_progress: 'primary',
  on_hold: 'warning',
  completed: 'success',
  not_started: 'neutral',
};

const TASK_STATUS_COLORS: Record<string, 'danger' | 'warning' | 'primary' | 'neutral'> = {
  blocked: 'danger',
  in_progress: 'primary',
  not_started: 'neutral',
};

const PRIORITY_COLORS: Record<string, 'danger' | 'warning' | 'primary' | 'neutral'> = {
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
};

interface ProjectCardProps {
  project: ProjectOverviewEntry;
  expandable?: boolean;
}

export default function ProjectCard({ project, expandable = false }: ProjectCardProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const isOverdue = project.end_date && new Date(project.end_date) < new Date();
  const progress = project.total_tasks > 0
    ? Math.round((project.completed_tasks / project.total_tasks) * 100)
    : 0;

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0, flex: 1, cursor: 'pointer' }} onClick={() => navigate(`/projects/${project.id}`)}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography level="body-sm" fontWeight="lg" noWrap>
                {project.name}
              </Typography>
              <Chip size="sm" variant="soft" color={STATUS_COLORS[project.status] || 'neutral'}>
                {project.status.replace('_', ' ')}
              </Chip>
              {isOverdue && (
                <Chip size="sm" variant="soft" color="danger">overdue</Chip>
              )}
            </Stack>
          </Box>

          {expandable && project.tasks && project.tasks.length > 0 && (
            <IconButton size="sm" variant="plain" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" mt={1}>
          <Box sx={{ flex: 1, minWidth: 80 }}>
            <LinearProgress
              determinate
              value={progress}
              color={project.blocked_tasks > 0 ? 'danger' : 'primary'}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
          <Typography level="body-xs" color="neutral" sx={{ flexShrink: 0 }}>
            {project.completed_tasks} / {project.total_tasks} tasks
          </Typography>
          {project.blocked_tasks > 0 && (
            <Chip size="sm" variant="soft" color="danger">
              {project.blocked_tasks} blocked
            </Chip>
          )}
        </Stack>

        {project.end_date && (
          <Typography level="body-xs" color={isOverdue ? 'danger' : 'neutral'} mt={0.5}>
            {isOverdue ? 'Was due' : 'Due'}: {new Date(project.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </Typography>
        )}

        {project.assigned_employees.length > 0 && (
          <Typography level="body-xs" color="neutral" mt={0.5}>
            Team: {project.assigned_employees.map(e => `${e.first_name} ${e.last_name}`).join(', ')}
          </Typography>
        )}

        {expanded && project.tasks && project.tasks.length > 0 && (
          <Box sx={{ mt: 1.5, overflowX: 'auto' }}>
            <Table size="sm" stripe="odd" sx={{ '& th': { py: 0.5 }, '& td': { py: 0.5 } }}>
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Priority</th>
                  <th>Assigned To</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {project.tasks.map(task => (
                  <tr key={task.id}>
                    <td>
                      <Typography level="body-xs">{task.name}</Typography>
                    </td>
                    <td>
                      <Chip size="sm" variant="soft" color={PRIORITY_COLORS[task.priority] || 'neutral'}>
                        {task.priority}
                      </Chip>
                    </td>
                    <td>
                      <Typography level="body-xs" color={task.assignees.length === 0 ? 'warning' : 'neutral'}>
                        {task.assignees.length > 0
                          ? task.assignees.map(a => `${a.first_name} ${a.last_name}`).join(', ')
                          : 'Unassigned'}
                      </Typography>
                    </td>
                    <td>
                      <Chip size="sm" variant="soft" color={TASK_STATUS_COLORS[task.status] || 'neutral'}>
                        {task.status.replace('_', ' ')}
                      </Chip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
